// ts-rest contract for the unified review-queue surface.
//
// Phase 2 of the /api/db/:table removal (OLD_CODE.html #1). The web hook
// `use-review-queue.ts` previously made 13 separate `useTable` calls
// against the generic CRUD surface to assemble the "items pending
// review" list. This endpoint replaces those reads with a single
// persona-gated server-side aggregation.
//
// The server-side service:
//   - reads every artefact summary table filtered to `reviewStatus =
//     'pending'`,
//   - denormalises `clientName` from the Client ontology so the hook
//     does not need a separate clients fetch,
//   - returns rows keyed by table name so the hook's existing merge
//     logic (collapse post-call artefacts per call, group composer
//     drafts, etc.) can run unchanged.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { coacActionDraftRowSchema } from './coac';

const c = initContract();

const reviewStatusSchema = z
    .enum(['pending', 'approved', 'rejected', 'revision_requested'])
    .nullable()
    .optional();

/**
 * Common shape every artefact row honours after the runtime writes it via
 * `create_artefact` — `id`, `reviewStatus`, plus a small set of common
 * content fields used to build the queue-item summary line. The hook
 * reads only these. Per-artefact-type detail panels resolve the rest via
 * their own typed endpoint or `useRow`.
 */
export const artefactSummaryRowSchema = z.object({
    id: z.string(),
    reviewStatus: reviewStatusSchema,
    /** Denormalised on the server for the queue summary line. */
    clientId: z.string().nullable().optional(),
    clientName: z.string().nullable().optional(),
    // Content fields the summary line falls back to in priority order.
    summary: z.string().nullable().optional(),
    subject: z.string().nullable().optional(),
    headline: z.string().nullable().optional(),
    claim: z.string().nullable().optional(),
    ruleStatement: z.string().nullable().optional(),
    signalSummary: z.string().nullable().optional(),
    // Composer drafts carry tonal variants — pick variants[0] when present.
    variants: z
        .array(z.object({ body: z.string().nullable().optional() }).nullable().optional())
        .nullable()
        .optional(),
});
export type ArtefactSummaryRow = z.infer<typeof artefactSummaryRowSchema>;

/** Post-call artefact rows — keyed by `callId` for per-call grouping. */
export const callArtefactRowSchema = z.object({
    id: z.string(),
    callId: z.string(),
    clientId: z.string().nullable().optional(),
    clientName: z.string().nullable().optional(),
    // The PERSON the insight belongs to (SEP-183 / ADR-0015). Resolved once at
    // attribution time and carried on the call, never re-matched on a read
    // path. Null for the third of the book that resolves to no Contact.
    contactId: z.string().nullable().optional(),
    contactName: z.string().nullable().optional(),
    reviewStatus: reviewStatusSchema,
});
export type CallArtefactRow = z.infer<typeof callArtefactRowSchema>;

/**
 * One counterparty CANDIDATE for a relationship proposal (SEP-215).
 *
 * It is a SUGGESTION and it binds nothing. The server ranks the reviewer's own
 * Contacts against the proposed name so the picker can open on a short list;
 * the reviewer's explicit pick is the only thing that reaches the write
 * boundary. `apps/server/src/lib/desk/match-contact.ts` states the rule: a
 * telephone number is evidence, a matching surname is not.
 */
export const counterpartySuggestionSchema = z.object({
    contactId: z.string(),
    name: z.string(),
    /** Why the server offered this Contact, in words the reviewer reads. */
    reason: z.string(),
});
export type CounterpartySuggestion = z.infer<typeof counterpartySuggestionSchema>;

/**
 * A pending relationship proposal, as the review queue shows it (SEP-215).
 *
 * It follows `callArtefactRowSchema`, because a proposal is an ATLAS post-call
 * artefact and it groups under the same call. `contactId` is the SUBJECT
 * person; the counterparty is deliberately absent, because only the reviewer
 * binds it.
 */
export const relationshipProposalRowSchema = z.object({
    id: z.string(),
    callId: z.string(),
    clientId: z.string().nullable().optional(),
    clientName: z.string().nullable().optional(),
    /** The subject person. Null when the Contact is outside the reader's book,
     *  or when the call resolved to none. */
    contactId: z.string().nullable().optional(),
    contactName: z.string().nullable().optional(),
    /** The other person, as the transcript named them. It binds nothing. */
    personName: z.string(),
    /** A RelationshipRole catalogue key with `appliesTo: 'contact_contact'`. */
    proposedRole: z.string(),
    proposedRoleLabel: z.string().nullable().optional(),
    sourceQuote: z.string().nullable().optional(),
    /** How sure the agent was. Null on a hand-added proposal, which states no
     *  confidence because a person wrote it. */
    confidence: z.enum(['high', 'medium', 'low']).nullable().optional(),
    /** The Relationship an approval produced, or null while it waits. */
    promotedRelationshipId: z.string().nullable().optional(),
    /** Ranked, non-binding counterparty candidates for the picker. */
    counterpartySuggestions: z.array(counterpartySuggestionSchema),
    reviewStatus: reviewStatusSchema,
});
export type RelationshipProposalRow = z.infer<typeof relationshipProposalRowSchema>;

/** Monthly narrative drafts — keyed by `(clientId, period)`. */
export const monthlyNarrativeRowSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    clientName: z.string().nullable().optional(),
    period: z.string(),
    reviewStatus: reviewStatusSchema,
});
export type MonthlyNarrativeRow = z.infer<typeof monthlyNarrativeRowSchema>;

export const reviewQueuePendingResponseSchema = z.object({
    // Post-call artefacts grouped per call.
    callExtractedFacts: z.array(callArtefactRowSchema),
    callActionItems: z.array(callArtefactRowSchema),
    callKbNotes: z.array(callArtefactRowSchema),
    // ATLAS person-to-person relationship proposals (SEP-215). They group under
    // the same call as the three rows above.
    relationshipProposals: z.array(relationshipProposalRowSchema),
    // ATLAS monthly narrative.
    wealthMonthlyNarrativeDrafts: z.array(monthlyNarrativeRowSchema),
    // CEDAR — CA notices extracted from an uploaded PDF, editable before
    // approve (ADR-0020), so the row carries its full structured field set
    // rather than the generic summary projection.
    coacActionDrafts: z.array(coacActionDraftRowSchema),
    // VIGIL + detector findings.
    wealthVigilFindings: z.array(artefactSummaryRowSchema),
    onboardingStallFindings: z.array(artefactSummaryRowSchema),
    vulnerabilityMarkerFindings: z.array(artefactSummaryRowSchema),
    pcfDeviationFindings: z.array(artefactSummaryRowSchema),
    investmentTrustPolicyFindings: z.array(artefactSummaryRowSchema),
    dataFreshnessFindings: z.array(artefactSummaryRowSchema),
    // Composer drafts (SCRIVENER / HERALD / EMISSARY / CHARTER / HAVEN /
    // FORUM / ARBITER).
    suitabilityLetterDrafts: z.array(artefactSummaryRowSchema),
    onboardingPackDrafts: z.array(artefactSummaryRowSchema),
    clientCommunicationDrafts: z.array(artefactSummaryRowSchema),
    fundReportDrafts: z.array(artefactSummaryRowSchema),
    vulnerabilityAdaptationPlanDrafts: z.array(artefactSummaryRowSchema),
    governancePaperDrafts: z.array(artefactSummaryRowSchema),
    fairValueAssessmentDrafts: z.array(artefactSummaryRowSchema),
});
export type ReviewQueuePendingResponse = z.infer<typeof reviewQueuePendingResponseSchema>;

/**
 * Per-frontend pending-review tallies for the always-mounted shell chrome
 * (sidebar "Review queue" badge + agent-panel peek). `itemCount` is the
 * number of review *items* (post-call drafts collapse to one item per
 * call, composer drafts / findings / tasks are one each); `decisionCount`
 * is the number of individual approve/reject decisions those items carry.
 *
 * This is the lightweight counterpart to `pending`: it returns only
 * numbers, never artefact rows or grouped review items, so the chrome can
 * show an accurate badge without assembling the full review-queue model
 * (PRD #156). The full `pending` payload remains the source of truth for
 * the review-queue page and the agent review surfaces.
 */
export const frontendReviewCountsSchema = z.object({
    itemCount: z.number().int().nonnegative(),
    decisionCount: z.number().int().nonnegative(),
});
export type FrontendReviewCounts = z.infer<typeof frontendReviewCountsSchema>;

export const reviewQueueCountsResponseSchema = z.object({
    desk: frontendReviewCountsSchema,
    ops: frontendReviewCountsSchema,
    comply: frontendReviewCountsSchema,
    view: frontendReviewCountsSchema,
});
export type ReviewQueueCountsResponse = z.infer<typeof reviewQueueCountsResponseSchema>;

// -----------------------------------------------------------------------
// Review context — the rich source context a review panel shows above the
// decision. The agent's own output is already on screen; this is the LIVE
// source row(s) it was reviewed against (the call, the break, the mandate
// state, …) which neither composeAgentTaskView nor the queue aggregator
// joins. Delivered lazily per open panel and persona-gated server-side.
//
// Normalised blocks + one generic renderer: most blocks are simple
// label/value `fields`; a few richer types (transcript excerpt, side-by-side
// break evidence) exist only where a kind needs them. The renderer drops any
// absent/empty value, so there is never filler — a kind with no extra useful
// context returns no blocks and the panel shows no context header.
// -----------------------------------------------------------------------

/** A label/value pair. Rendered only when `value` is a non-empty string. */
export const contextFieldSchema = z.object({
    label: z.string(),
    value: z.string().nullable().optional(),
    /** Optional emphasis hint for the renderer (e.g. a status pill colour). */
    tone: z.enum(['default', 'positive', 'warning', 'negative']).optional(),
});
export type ContextField = z.infer<typeof contextFieldSchema>;

/** One transcript line; `cited` marks a line an artefact extracted from. */
export const contextTranscriptLineSchema = z.object({
    lineId: z.string().nullable().optional(),
    speaker: z.string().nullable().optional(),
    timestamp: z.string().nullable().optional(),
    text: z.string(),
});
export type ContextTranscriptLine = z.infer<typeof contextTranscriptLineSchema>;

export const contextBlockSchema = z.discriminatedUnion('type', [
    // Label/value group (the common case). `subject` flags the block as the
    // primary subject header so the renderer can style it more prominently.
    z.object({
        type: z.literal('fields'),
        title: z.string().optional(),
        subject: z.boolean().optional(),
        fields: z.array(contextFieldSchema),
    }),
    // A longer free-text passage (prior narrative, rule statement, basis).
    z.object({
        type: z.literal('prose'),
        title: z.string().optional(),
        text: z.string(),
    }),
    // Reconciliation-style side-by-side evidence.
    z.object({
        type: z.literal('sideBySide'),
        title: z.string().optional(),
        custodian: z.string().nullable().optional(),
        internal: z.string().nullable().optional(),
        diff: z.string().nullable().optional(),
    }),
    // Full call transcript; the renderer locates cited lines and expands
    // surrounding context on demand.
    z.object({
        type: z.literal('transcriptExcerpt'),
        title: z.string().optional(),
        lines: z.array(contextTranscriptLineSchema),
    }),
    // A linked entity chip (optionally a deep link).
    z.object({
        type: z.literal('linkedEntity'),
        label: z.string(),
        value: z.string(),
        href: z.string().nullable().optional(),
    }),
]);
export type ContextBlock = z.infer<typeof contextBlockSchema>;

export const reviewContextResponseSchema = z.object({
    blocks: z.array(contextBlockSchema),
});
export type ReviewContextResponse = z.infer<typeof reviewContextResponseSchema>;

export const reviewContextQuerySchema = z.object({
    // The context subject: a ReviewableKind (artefact id in `id`) or the
    // synthetic `'call'` subject (callId in `id`).
    subjectKind: z.string(),
    id: z.string(),
});
export type ReviewContextQuery = z.infer<typeof reviewContextQuerySchema>;

export const reviewQueueContract = c.router(
    {
        pending: {
            method: 'GET',
            path: '/review-queue/pending',
            responses: {
                200: reviewQueuePendingResponseSchema,
            },
            summary: 'Aggregated per-table summary of every artefact row pending human review',
        },
        counts: {
            method: 'GET',
            path: '/review-queue/counts',
            responses: {
                200: reviewQueueCountsResponseSchema,
            },
            summary: 'Lightweight per-frontend pending-review counts for shell chrome badges',
        },
        context: {
            method: 'GET',
            path: '/review-queue/context',
            query: reviewContextQuerySchema,
            responses: {
                200: reviewContextResponseSchema,
            },
            summary:
                'Rich live source context for an open review panel (joined subject rows, persona-gated)',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
